import { cameraFromHash, cameraToHash, INITIAL_CAMERA } from "./camera.js";
import { PRODUCTION_ATLAS_CONTRACT, type AtlasContract } from "./atlasContract.js";
import { loadAtlasLevelWithManifestRetry, loadTycoonAssetMetadata, loadTycoonAssets, type AtlasImage, type TycoonAssetMetadata, type TycoonAssets } from "./assets.js";
import {
  type CameraState,
  geoToTile,
  geoToWorld,
  rotate,
  screenToTile,
  tileToScreen,
  worldToTile,
} from "./projection.js";
import { attachTycoonInput } from "./input.js";
import { consistCardHtml, createTycoonHud } from "./hud.js";
import { Visibility } from "../hud/visibility.js";
import type { ClientStore } from "../state/clientStore.js";
import type { ServerClock } from "../state/clock.js";
import {
  ALIGNMENT_HEIGHT,
  projectMatchOnPath,
  selectDirectedVariant,
} from "./track.js";
import { FleetAssignments } from "./fleet.js";
import { placeConsist } from "./consists.js";
import {
  createTycoonMotion,
  type TycoonMotion,
} from "./motion.js";
import { boundsIntersect, SpatialIndex, type AtlasSpriteId, type SpriteDrawable } from "./scene.js";
import type { SpriteId } from "./contracts.js";
import { buildStaticWorld, primitiveFrame, type StaticLayer, type StaticPrimitive } from './staticWorld.js';
import { clipPathFor } from './rampWorld.js';
import { atlasHeadingCount, CarPosePool, carFragment, cityFragment, SceneOrderer, staticFragment, type CarNodeInput, type CarPoseGroup } from './sceneOrder.js';
import { buildStaticGraph, type StaticGraph } from './precedenceGraph.js';
import { createCompositionIndex, type CompositionIndex } from './compositionIndex.js';
import {
  decodeTerrainImage,
  terrainOpacity,
  TerrainPainter,
} from "./terrain.js";
import { fetchSubwayGeometry } from "../layers/subwayMap.js";
import { fetchStationExtents } from "../layers/stationExtents.js";
import {
  buildStations,
  stationPipelineStats,
  type StationPipelineStats,
  type TycoonStation,
} from "./stations.js";
import { type CarOverlay, carOverlays } from "./overlays.js";
import { stationCardHtml } from "./cards.js";
import { spriteFrameIndex } from "./rotations.js";
import { atlasDrawScale, requestedAtlasLevel } from './levels.js';
import { AtlasLevelLifecycle } from './atlasLifecycle.js';
import type { SubwayStationArrival } from "@vitals/shared";
import { trainNearViewport } from "./culling.js";
import { advanceProjectedHead, decideTrain, emptyTrainDecisionCounts, readTrainFeature, TrainFeatureValidationError } from "./trainPipeline.js";
import { buildingClearance, CityChunks } from './city.js';
import { NO_CLEARED_TILES, type ClearedTiles } from './buildingClearance.js';
import { createCanvasRendererAdapter } from './canvasRendererAdapter.js';
import type { RenderDecoration, RenderPose, RendererAdapterFactory, TycoonRendererAdapter } from './rendererAdapter.js';
import { withCanvasRendererReadback } from './rendererReadback.js';
import { renderFrame, spriteSubmission } from './renderFrame.js';
import { cameraTranslation } from './submissionGeometry.js';
import { createAtlasRequestScheduler } from './atlasRequestScheduler.js';

export const visibleCityChunkRange = (
  bounds: readonly [number, number, number, number], raster: { width: number; height: number }, _rotation: import('./projection.js').Rotation,
): readonly [number, number, number, number] | undefined => {
  const cols = Math.ceil(raster.width / 16), rows = Math.ceil(raster.height / 16), minX = Math.max(0, Math.floor((bounds[0] - 8) / 16)), minY = Math.max(0, Math.floor((bounds[1] - 8) / 16)), maxX = Math.min(cols - 1, Math.ceil((bounds[2] + 8) / 16)), maxY = Math.min(rows - 1, Math.ceil((bounds[3] + 8) / 16));
  return minX > maxX || minY > maxY ? undefined : [minX, minY, maxX, maxY];
};

/** Crossing zones a car's proxy footprint (converted to world metres) intersects on its corridor. */
function carCrossings(index: CompositionIndex, corridorId: string, tile: readonly [number, number], heading: number, pitchMeters: number, widthMeters: number): string[] {
  const c = Math.cos(heading), s = Math.sin(heading), l = pitchMeters / 40, t = widthMeters / 40;
  const footprint = ([[-l, -t], [l, -t], [l, t], [-l, t]] as const).map(([dl, dt]) => { const x = tile[0] + dl * c - dt * s, y = tile[1] + dl * s + dt * c; return [x * 20 - 26000, 28000 - y * 20] as const; });
  return index.crossingsFor(corridorId, footprint);
}

export interface TycoonView {
  ready: Promise<void>;
  getState(): CameraState;
  frame(): void;
  destroy(): void;
}
interface CarTarget {
  bounds: [number, number, number, number];
  html: string;
  tripId: string;
}
/** Retained static chunk range for the precedence graph: 16-tile chunks padded like the city range, unclamped. */
const graphChunkRange = (bounds: readonly [number, number, number, number]): [number, number, number, number] =>
  [Math.floor((bounds[0] - 8) / 16), Math.floor((bounds[1] - 8) / 16), Math.ceil((bounds[2] + 8) / 16), Math.ceil((bounds[3] + 8) / 16)];
export function createTycoonView(
  options: {
    root: HTMLElement;
    initialState?: CameraState;
    loadAssets?: typeof loadTycoonAssets;
    visibility?: Visibility;
    navigateMap?: () => void;
    store?: ClientStore;
    clock?: ServerClock;
    motion?: TycoonMotion;
    loadAtlasLevel?: typeof loadAtlasLevelWithManifestRetry;
    /** Heading contract every non-baseline atlas level must satisfy; production is 64 headings. */
    atlasContract?: AtlasContract;
    rendererFactory?: RendererAdapterFactory;
    onFrameTiming?: (timing: {simulation:number;ordering:number;submission:number;total:number}) => void;
  },
): TycoonView {
  let state = options.initialState ??
      cameraFromHash(location.hash, INITIAL_CAMERA),
    destroyed = false,
    carTargets: CarTarget[] = [],
    lastFrameAt = performance.now(),
    staticCameraKey = "",
    pixelRatio = devicePixelRatio || 1,
    failedAtlasIntent: number | undefined,
    stationPinned = false,
    hoveredTrip: string | undefined,
    frameId = 0;
  const canvas = document.createElement("canvas");
  canvas.className = "tycoon-canvas";
  canvas.setAttribute("aria-label", "Tycoon subway view");
  options.root.append(canvas);
  const onContextLost=(event:Event)=>{event.preventDefault();if(!destroyed)canvas.dataset.rendererError='WebGL context lost';};
  const onContextRestored=()=>{if(!destroyed)delete canvas.dataset.rendererError;};
  // Register before Pixi so its own context handlers cannot hide lifecycle state.
  canvas.addEventListener('webglcontextlost',onContextLost,true);canvas.addEventListener('webglcontextrestored',onContextRestored,true);
  let renderer: TycoonRendererAdapter | undefined;
  const rendererReady=(options.rendererFactory ?? createCanvasRendererAdapter)(withCanvasRendererReadback({
    canvas,
    onDeviceLost: (reason) => { if (!destroyed){
      if(reason instanceof Event&&reason.type==='webglcontextlost'){canvas.dataset.rendererError='WebGL context lost';return;}
      if(reason instanceof Event&&reason.type==='webglcontextrestored'){delete canvas.dataset.rendererError;atlasScheduler.update(Number.NaN);atlasScheduler.update(state.zoom*pixelRatio);return;}
      canvas.dataset.rendererError=String(reason);queueMicrotask(()=>{if(destroyed)return;levelLifecycle?.dispose();levelLifecycle=undefined;void load();});
    } },
  })).then((created) => { if (destroyed) created.destroy(); else{renderer=created;canvas.dataset.rendererBackend=created.backend;} });
  const visibility = options.visibility ?? new Visibility();
  let assets: TycoonAssets | undefined,
    terrain: TerrainPainter | undefined,
    staticDrawables: SpriteDrawable[] = [],
    staticWorld: StaticPrimitive[] = [],
    staticIndex: SpatialIndex<StaticPrimitive> | undefined,
    staticGraph: StaticGraph | undefined,
    staticVersion = 0,
    compositionIndex: CompositionIndex | undefined,
    compositionVersion = 0,
    cityChunks: CityChunks | undefined,
    levelLifecycle: AtlasLevelLifecycle | undefined,
    terrainRaster: ReturnType<typeof decodeTerrainImage> | undefined,
    clearedBuildings: ClearedTiles = NO_CLEARED_TILES,
    stations: TycoonStation[] = [],
    stationStats: StationPipelineStats | undefined;
  let staticPoses = new Map<string, RenderPose>();
  const orderer = new SceneOrderer();
  let lastBrokenSignature = '';
  const assignments = new FleetAssignments(),
    variants = new Map<string, string>(),
    projectedDistances = new Map<string, number>();
  const motion = options.motion ?? createTycoonMotion();
  const commitCamera=(next:CameraState)=>history.replaceState(null,"",`${location.pathname}${location.search}${cameraToHash(next)}`);
  const input = attachTycoonInput(canvas, () => state, (next) => {
    state = next;
  },{onGestureEnd:commitCamera});
  const applyExternalCamera=()=>{
    input.cancel();
    state=cameraFromHash(location.hash,state);
    staticCameraKey='';
  };
  const hasWindowEvents=typeof window.addEventListener==='function';
  if(hasWindowEvents){window.addEventListener('hashchange',applyExternalCamera);window.addEventListener('popstate',applyExternalCamera);}
  const hud = createTycoonHud(
    options.root,
    visibility,
    options.navigateMap ?? (() => {}),
  );
  const spriteEntry = (id: AtlasSpriteId) => {
    const entry = assets!.atlas.sprites[id as SpriteId] ?? assets!.atlas.components?.[id];
    if (!entry) throw new Error(`atlas has no sprite ${id}`);
    return entry;
  };
  const frameFor = (id: AtlasSpriteId, heading: number) =>
    spriteFrameIndex(id, spriteEntry(id).rotations, heading, state.rotation);
  const spriteDrawable = (
    id: AtlasSpriteId,
    tile: [number, number],
    heading: number,
    height: number,
    stableId: string,
    clip?: "back" | "front",
    explicitFrame?: number,
  ): SpriteDrawable => {
    const sprite = spriteEntry(id),
      frameIndex = explicitFrame ?? frameFor(id, heading),
      frame = sprite.frames[frameIndex]!,
      relative = rotate(
        [tile[0] - state.center[0], tile[1] - state.center[1]],
        state.rotation,
      ),
      screenScale = state.zoom * pixelRatio,
      scale = atlasDrawScale(screenScale, assets!.atlas.level ?? 1),
      screen = tileToScreen(
        tile,
        { ...state, zoom: screenScale as 1 | 2 | 3 },
        height,
      ),
      x = screen[0] + canvas.width / 2 - frame.anchor[0] * scale,
      y = screen[1] + canvas.height / 2 - frame.anchor[1] * scale;
    return {
      spriteId: id,
      frameIndex,
      rotatedX: relative[0],
      rotatedY: relative[1],
      height,
      stableId,
      bounds: [
        x, y, x + frame.rect[2] * scale, y + frame.rect[3] * scale,
      ],
      rect: frame.rect,
      page: frame.page,
      anchor: frame.anchor,
      screen: [screen[0] + canvas.width / 2, screen[1] + canvas.height / 2],
      scale,
      clip,
    };
  };
  const shown = (alignment: string) =>
    (alignment === "tunnel" || alignment === "openCut")
      ? visibility.isVisible("tycoon.underground")
      : visibility.isVisible("tycoon.surface");
  /** Ramps join both layers: visible when either is on, so a classification switch never hides them. */
  const rampShown = () => visibility.isVisible("tycoon.surface") || visibility.isVisible("tycoon.underground");
  const layerShown = (layer: StaticLayer) =>
    layer === 'ramp' ? rampShown() : visibility.isVisible(layer === 'underground' ? "tycoon.underground" : "tycoon.surface");
  const projectStatic = () => {
    if (!staticIndex) return;
    const scale = state.zoom * pixelRatio,
      camera = { ...state, zoom: scale as 1 | 2 | 3 },
      corners: [
        [number, number],
        [number, number],
        [number, number],
        [number, number],
      ] = [
        [-canvas.width / 2, -canvas.height / 2],
        [canvas.width / 2, -canvas.height / 2],
        [-canvas.width / 2, canvas.height / 2],
        [canvas.width / 2, canvas.height / 2],
      ],
      tiles = corners.map((corner) => screenToTile(corner, camera)),
      bounds: [number, number, number, number] = [
        Math.min(...tiles.map((tile) => tile[0])) - 4,
        Math.min(...tiles.map((tile) => tile[1])) - 4,
        Math.max(...tiles.map((tile) => tile[0])) + 4,
        Math.max(...tiles.map((tile) => tile[1])) + 4,
      ];
    const visibleWorld = staticIndex.query(bounds).filter((item) => layerShown(item.layer) && !item.hiddenIn?.(state.rotation));
    const city = !cityChunks || !visibility.isVisible('tycoon.surface') ? [] : (() => {
      const range = visibleCityChunkRange(bounds, cityChunks.dimensions, state.rotation);
      if (!range) return [];
      const [minChunkX, minChunkY, maxChunkX, maxChunkY] = range;
      const seeds = [] as ReturnType<CityChunks['get']>[number][];
      for (let chunkY = minChunkY; chunkY <= maxChunkY; chunkY += 1) for (let chunkX = minChunkX; chunkX <= maxChunkX; chunkX += 1) {
        seeds.push(...cityChunks.get(chunkX, chunkY, state.rotation, state.zoom, pixelRatio));
      }
      return visibility.isVisible('tycoon.buildings') ? seeds : seeds.filter((seed) => !seed.stableId.startsWith('building:'));
    })();
    if (stationStats) {
      stationStats = {
        ...stationStats,
        cullingSurvivors: new Set(visibleWorld.flatMap((item) =>
          item.occlusionGroup?.startsWith("station:") ? [item.occlusionGroup] : []
        )).size,
      };
      canvas.dataset.stationPipelineStats = JSON.stringify(stationStats);
    }
    const cityOrigin = tileToScreen(state.center, { center: [0, 0], rotation: state.rotation, zoom: scale as 1 | 2 | 3 });
    staticPoses = new Map();
    staticDrawables = [...visibleWorld.map((item) => {
      staticPoses.set(item.stableId,{tile:[...item.tile],worldMeters:[item.tile[0]*20-26000,28000-item.tile[1]*20],alignment:item.alignment,elevation:item.height,layerVisible:true});
      const spriteId = item.sprite(state.rotation);
      return {
      ...spriteDrawable(
        spriteId,
        [...item.tile],
        item.heading,
        item.height,
        item.stableId,
        item.clip,
        primitiveFrame(item, state.rotation, spriteEntry(spriteId).rotations, atlasHeadingCount(assets!.atlas)),
      ),
      sortHeight: item.sortHeight,
      occlusionGroup: item.occlusionGroup,
      ...(item.clipOutline ? { clipPath: clipPathFor(item, state.rotation) } : {}),
    };}), ...city.map((item) => {
      staticPoses.set(item.stableId,{tile:item.tile,worldMeters:[item.tile[0]*20-26000,28000-item.tile[1]*20],alignment:'atGrade',elevation:0,layerVisible:true});
      const x = item.screen[0] - cityOrigin[0] + canvas.width / 2, y = item.screen[1] - cityOrigin[1] + canvas.height / 2;
      return { spriteId: item.spriteId, rotatedX: item.rotatedX, rotatedY: item.rotatedY, height: 0, stableId: item.stableId, bounds: [x + item.boundsOffset[0], y + item.boundsOffset[1], x + item.boundsOffset[2], y + item.boundsOffset[3]] as [number,number,number,number], rect: [...item.rect] as [number,number,number,number], anchor: [...item.anchor] as [number,number], screen: [x,y] as [number,number], scale: atlasDrawScale(scale, assets!.atlas.level ?? 1), page:item.page };
    })];
    // Precedence graph over the retained chunk set: layer toggles and viewport culling only hide its nodes.
    const range = graphChunkRange(bounds), atlas = assets!.atlas, rotation = state.rotation;
    const graphKey = JSON.stringify([rotation, compositionVersion, range, atlas.sourceRevision, atlas.level, staticVersion]);
    staticGraph = orderer.graph(graphKey, rotation, compositionIndex?.strata() ?? [], () => {
      const world = staticIndex!.query([range[0] * 16, range[1] * 16, (range[2] + 1) * 16, (range[3] + 1) * 16]).filter((item) => !item.hiddenIn?.(rotation));
      const seeds = [] as ReturnType<CityChunks['get']>[number][];
      if (cityChunks) {
        const cols = Math.ceil(cityChunks.dimensions.width / 16), rows = Math.ceil(cityChunks.dimensions.height / 16);
        for (let chunkY = Math.max(0, range[1]); chunkY <= Math.min(rows - 1, range[3]); chunkY += 1) for (let chunkX = Math.max(0, range[0]); chunkX <= Math.min(cols - 1, range[2]); chunkX += 1) seeds.push(...cityChunks.get(chunkX, chunkY, rotation, state.zoom, pixelRatio));
      }
      return [...world.map((item) => staticFragment(item, rotation, atlas)), ...seeds.map((seed) => cityFragment(seed, rotation, atlas))];
    });
    staticCameraKey = JSON.stringify([
      state,
      canvas.width,
      canvas.height,
      pixelRatio,
    ]);
  };
  const rebuildStatic = () => {
    if (!assets) return;
    const paths = Object.values(assets.alignment.routes).flat();
    staticWorld = buildStaticWorld({ paths, stations, composition: compositionIndex, atlas: assets.atlas });
    staticVersion += 1;
    staticIndex = new SpatialIndex(staticWorld, 32);
    projectStatic();
  };
  const assetError = document.createElement("div");
  assetError.className = "tycoon-asset-error";
  assetError.setAttribute("role", "alert");
  assetError.hidden = true;
  const errorMessage = document.createElement("p"),
    retryButton = document.createElement("button"),
    backButton = document.createElement("button");
  retryButton.type = backButton.type = "button";
  retryButton.textContent = "Retry";
  backButton.textContent = "Back to map";
  const navigateMap = options.navigateMap ?? (() => {});
  backButton.addEventListener("click", navigateMap);
  assetError.append(errorMessage, retryButton, backButton);
  options.root.append(assetError);
  let loadGeneration = 0, atlasIntentGeneration = 0;
  const initializingLifecycles = new Set<AtlasLevelLifecycle>();
  const atlasScheduler=createAtlasRequestScheduler({
    supersede:()=>{atlasIntentGeneration++;levelLifecycle?.supersedePending();},
    request:async(level)=>{
      const lifecycle=levelLifecycle,generation=atlasIntentGeneration;
      const activated=await lifecycle?.request(level)??false;
      if(destroyed || generation!==atlasIntentGeneration || lifecycle!==levelLifecycle)return false;
      if(activated || levelLifecycle?.active?.level===level){failedAtlasIntent=undefined;delete canvas.dataset.atlasLevelError;assetError.hidden=true;}
      else if(levelLifecycle?.active?.level!==level){failedAtlasIntent=level;const cause=levelLifecycle?.retentionFailure?.message,message=`Unable to activate atlas level ${level}${cause?`: ${cause}`:''}`;canvas.dataset.atlasLevelError=message;errorMessage.textContent=message;assetError.hidden=false;}
      return activated;
    },
  });
  const load = (): Promise<void> => {
    const generation = ++loadGeneration;
    for(const lifecycle of initializingLifecycles)lifecycle.dispose();
    initializingLifecycles.clear();
    return Promise.all([
      rendererReady,
      options.loadAssets ? options.loadAssets() : loadTycoonAssetMetadata(),
      fetchSubwayGeometry(import.meta.env.VITE_API_BASE ?? ""),
      fetchStationExtents(import.meta.env.VITE_API_BASE ?? ""),
    ]).then(async ([_renderer, loaded, geometry, extents]) => {
      if (destroyed || generation !== loadGeneration) return;
      // Composition is part of the asset loader's contract; its load or validation errors reject the loader and
      // surface through the asset error UI. A loader that leaves it undefined is reported, never treated as absent.
      const composition = (loaded as TycoonAssetMetadata).composition;
      if (composition === undefined) console.warn('tycoon: the asset loader did not supply composition (expected a decoded composition or null); drawing without it');
      compositionIndex = composition ? createCompositionIndex(composition) : undefined;
      compositionVersion += 1;
      canvas.dataset.composition = composition === undefined ? 'not-supplied' : composition ? 'loaded' : 'absent';
      assetError.hidden = true;
      const previousLifecycle=levelLifecycle;
      const raster = decodeTerrainImage(loaded.terrain);
      // Building tiles raised structures stand in (B-009), once per composition and raster.
      const clearance = buildingClearance(compositionIndex, raster);

      // Injected assets are caller-owned and may already contain a decoded baseline.
      const adopted = 'atlas' in loaded;
      let stagedAssets:TycoonAssets|undefined;
      if (adopted) { const decoded=loaded as TycoonAssets; stagedAssets = { ...decoded, atlasPages: [...(decoded.atlasPages ?? [decoded.atlasImage])] }; }
      let baselineAvailable=adopted;
      let replacementLifecycle:AtlasLevelLifecycle;
      replacementLifecycle=new AtlasLevelLifecycle(async(level,reserve,preferredManifest)=>{
        if(level===1&&baselineAvailable&&stagedAssets){baselineAvailable=false;const decodedBytes=stagedAssets.atlas.pages.reduce((sum,page)=>sum+page.sizePx[0]*page.sizePx[1]*4,0);reserve(decodedBytes);return{level,atlas:stagedAssets.atlas,pages:stagedAssets.atlasPages as unknown as {close?:()=>void}[],decodedBytes,owned:false,manifest:stagedAssets.manifest};}
        const currentManifest=preferredManifest??(levelLifecycle===replacementLifecycle?assets?.manifest:loaded.manifest)??loaded.manifest;
        const next=await (options.loadAtlasLevel??loadAtlasLevelWithManifestRetry)(currentManifest,level,{manifest:'/tycoon/atlas-manifest.json',atlasBase:loaded.atlasBase},reserve,options.atlasContract??PRODUCTION_ATLAS_CONTRACT);
        return{...next,level,pages:next.pages as unknown as {close?:()=>void}[],owned:true};
      },()=>{const active=replacementLifecycle.active;if(!active)return;const pages=active.pages as AtlasImage[],target=levelLifecycle===replacementLifecycle?assets:stagedAssets;if(!target)stagedAssets={...loaded,atlas:active.atlas,atlasPages:pages,atlasImage:pages[0]!} as TycoonAssets;else{if(active.manifest)target.manifest=active.manifest;target.atlas=active.atlas;target.atlasPages=pages;target.atlasImage=pages[0]!;}if(levelLifecycle===replacementLifecycle&&assets&&terrainRaster){terrain=new TerrainPainter(terrainRaster,assets.atlas,assets.atlasPages);cityChunks=new CityChunks(terrainRaster,assets.atlas,undefined,undefined,clearedBuildings);staticCameraKey='';rebuildStatic();}}, undefined, renderer);
      initializingLifecycles.add(replacementLifecycle);
      const initialized=await replacementLifecycle.initialize();
      initializingLifecycles.delete(replacementLifecycle);
      if(destroyed || generation!==loadGeneration){replacementLifecycle.dispose();return;}
      if(!initialized){replacementLifecycle.dispose();throw new Error(`baseline atlas failed to activate${replacementLifecycle.retentionFailure?`: ${replacementLifecycle.retentionFailure.message}`:''}`);}
      terrainRaster=raster;clearedBuildings=clearance;
      levelLifecycle=replacementLifecycle;
      atlasScheduler.update(Number.NaN);
      atlasScheduler.update(state.zoom*pixelRatio);
      assets=stagedAssets;
      previousLifecycle?.dispose();
      if(!assets)throw new Error('baseline atlas failed to activate');
      terrain=new TerrainPainter(terrainRaster,assets.atlas,assets.atlasPages);cityChunks=new CityChunks(terrainRaster,assets.atlas,undefined,undefined,clearedBuildings);staticCameraKey='';rebuildStatic();
      // Let the next application frame paint decoded terrain before the
      // full-network static index is constructed. The index is immutable and
      // appears atomically on the following frame.
      requestAnimationFrame(() => {
        if (destroyed || generation !== loadGeneration) return;
        if (geometry) {
          const stationInputs = geometry.stations.features.map((station) => ({
              stationId: station.properties.id,
              selectionId: station.properties.selectionId,
              name: station.properties.name,
              position: station.geometry.coordinates,
            })), extentInputs = extents?.extents.map((extent) => ({
              stationId: extent.stationId,
              polygon: extent.detail.flat(),
            })) ?? [], paths = Object.values(loaded.alignment.routes).flat();
          stations = buildStations(stationInputs, extentInputs, paths);
          stationStats = stationPipelineStats(stationInputs, extentInputs, paths, () => false);
        }
        rebuildStatic();
      });
    }).catch((error: unknown) => {
      if (!destroyed && generation === loadGeneration) {
        const message = error instanceof Error ? error.message : String(error);
        canvas.dataset.error = message;
        errorMessage.textContent = `Unable to load Tycoon assets: ${message}`;
        assetError.hidden = false;
      }
    });
  };
  const retry = () => { failedAtlasIntent = undefined; delete canvas.dataset.atlasLevelError; atlasScheduler.update(Number.NaN); void load(); };
  retryButton.addEventListener("click", retry);
  const ready = load();
  const showCar = (event: PointerEvent) => {
    const ratio = devicePixelRatio || 1,
      x = event.offsetX * ratio,
      y = event.offsetY * ratio,
      target = [...carTargets].reverse().find((car) =>
        x >= car.bounds[0] && x <= car.bounds[2] && y >= car.bounds[1] &&
        y <= car.bounds[3]
      );
    hoveredTrip = target?.tripId;
    if (target) {
      stationPinned = false;
      hud.show(target.html, [event.clientX, event.clientY]);
    } else if (!stationPinned) hud.hide();
  };
  const showStation = (event: PointerEvent) => {
    if (!assets) return;
    const ratio = devicePixelRatio || 1,
      x = event.offsetX * ratio,
      y = event.offsetY * ratio,
      station = stations.find((candidate) => {
        if (!shown(candidate.alignment)) return false;
        const height = candidate.alignment === "elevated"
            ? 3
            : candidate.alignment === "embankment"
            ? 1
            : candidate.alignment === "atGrade"
            ? 0
            : -2,
          screen = tileToScreen(geoToTile(candidate.position), {
            ...state,
            zoom: (state.zoom * ratio) as 1 | 2 | 3,
          }, height);
        return Math.abs(x - (screen[0] + canvas.width / 2)) <
            48 * state.zoom * ratio &&
          Math.abs(y - (screen[1] + canvas.height / 2)) <
            32 * state.zoom * ratio;
      });
    if (!station) return;
    const summary = options.store?.get("subway")?.summary as {
      stationArrivals?: Record<string, SubwayStationArrival[]>;
    } | undefined;
    stationPinned = true;
    hud.show(
      stationCardHtml(
        station.name,
        summary?.stationArrivals?.[station.selectionId] ?? [],
        options.clock?.nowMs() ?? Date.now(),
      ),
      [event.clientX, event.clientY],
    );
  };
  canvas.addEventListener("pointermove", showCar);
  canvas.addEventListener("click", showStation);
  // Layer toggles only hide nodes: re-project the retained set, keep the static world and precedence graph.
  const offVisibility = visibility.onChange(() => { staticCameraKey = ''; });
  /**
   * Part B order: visible statics from the cached graph plus on-screen cars. Coincident cars share one fragment
   * (built, and crossing-tested, once per distinct pose). Returns the ordered scene, its trace precedence
   * resolver and the time spent in the precedence sort itself.
   */
  const orderScene = (dynamic: readonly SpriteDrawable[], carNodes: ReadonlyMap<string, CarNodeInput>, viewport: [number, number, number, number]) => {
    const atlas = assets!.atlas, graph = staticGraph ?? buildStaticGraph([], { rotation: state.rotation });
    const visibleStatic = new Set<string>(), byId = new Map<string, SpriteDrawable>();
    for (const item of staticDrawables) if (boundsIntersect(item.bounds, viewport)) {
      if (!graph.index.has(item.stableId)) throw new Error(`retained static ${item.stableId} is missing from the precedence graph`);
      visibleStatic.add(item.stableId); byId.set(item.stableId, item);
    }
    const pool = new CarPosePool(), groupOf = new Map<string, CarPoseGroup>();
    for (const item of dynamic) if (boundsIntersect(item.bounds, viewport)) { byId.set(item.stableId, item); groupOf.set(item.stableId, pool.add(carNodes.get(item.stableId)!)); }
    for (const group of pool.groups) if (compositionIndex && group.input.corridorId !== null) group.crossingIds = carCrossings(compositionIndex, group.input.corridorId, group.input.tile, group.input.heading, group.input.pitchMeters, group.input.widthMeters);
    const fragments = pool.groups.map((group) => carFragment(group, state.rotation, atlas));
    const orderingStart = performance.now(), ordered = orderer.order(graph, fragments, visibleStatic), orderingMs = performance.now() - orderingStart;
    canvas.dataset.precedenceStats = JSON.stringify(ordered.stats);
    // Dev log once per distinct set of broken edges, not every frame.
    if (import.meta.env?.DEV && ordered.stats.brokenCycles) {
      const signature = ordered.result.brokenEdges.map((edge) => `${edge.before}>${edge.after}`).join('|');
      if (signature !== lastBrokenSignature) console.warn(`tycoon precedence: broke ${ordered.stats.brokenCycles} cycle edge(s)`, ordered.result.brokenEdges);
      lastBrokenSignature = signature;
    }
    const crossingsOf = (id: string) => groupOf.get(id)?.crossingIds ?? graph.nodes[graph.index.get(id)!]!.fragment.crossingIds;
    return { scene: ordered.order.map((id) => byId.get(id)!), precedenceFor: (item: SpriteDrawable) => ordered.precedence(item.stableId, crossingsOf(item.stableId)), orderingMs };
  };
  const frame = () => {
    if (destroyed || !assets) return;
    const now = performance.now(), elapsed = (now - lastFrameAt) / 1000;
    lastFrameAt = now;
    const ratio = devicePixelRatio || 1,
      width = Math.round(innerWidth * ratio),
      height = Math.round(innerHeight * ratio);
    if(levelLifecycle?.active){
      if(hasWindowEvents)atlasScheduler.update(state.zoom*ratio);
      else {const requested=requestedAtlasLevel(state.zoom*ratio);if(requested&&requested!==levelLifecycle.active.level)void levelLifecycle.request(requested);}
    }
    if (
      canvas.width !== width || canvas.height !== height || pixelRatio !== ratio
    ) {
      canvas.width = width;
      canvas.height = height;
      pixelRatio = ratio;
      staticCameraKey = "";
    }
    if (
      staticCameraKey !==
        JSON.stringify([state, canvas.width, canvas.height, pixelRatio])
    ) projectStatic();
    const dynamic: SpriteDrawable[] = [], hiddenDynamic: SpriteDrawable[] = [], carNodes = new Map<string, CarNodeInput>(),
      dynamicPoses = new Map<string, import('./rendererAdapter.js').RenderPose>(),
      overlays = new Map<string, CarOverlay[]>();
    carTargets = [];
    let degradedTrips = 0;
    const trainDecisions = emptyTrainDecisionCounts();
    const subway = options.store?.get("subway"),
      activeIds = new Set((subway?.features ?? []).flatMap((feature) =>
        typeof feature?.id === "string" ? [feature.id] : []
      ));
    for (const feature of subway?.features ?? []) {
      try {
        const train = readTrainFeature(feature),
          route = train.routeId.toUpperCase(),
          candidates = assets.alignment.routes[route] ?? [],
          timestamps = train.timestamps ?? [],
          fleet = assignments.get(train.id, route, assets.fleet),
          pitch = assets.atlas.cars[fleet.class].pitchMeters,
          cachedPath = candidates.find((candidate) =>
            candidate.id === variants.get(train.id)
          ),
          decision = decideTrain(
            { id: train.id, path: train.path, timestamps },
            candidates,
            cachedPath,
            fleet.cars * pitch,
            () => motion.step(
              { id: train.id, path: train.path, timestamps },
              options.clock?.nowSec() ?? Date.now() / 1000,
              elapsed,
            ).position,
            (head) => trainNearViewport(head, state, [width, height], pixelRatio),
          );
        const path = decision.path;
        if (decision.decision !== "drawn") {
          trainDecisions[decision.decision] += 1;
          if (decision.decision === "culled") {
            const culledPath=path??selectDirectedVariant(train.path,candidates);
            const advanced = advanceProjectedHead(
              {...decision,path:culledPath},
              projectedDistances.get(train.id),
            );
            if (advanced !== undefined) projectedDistances.set(train.id, advanced);
            if (decision.head && culledPath) {
              variants.set(train.id,culledPath.id);
              const match=projectMatchOnPath(decision.head,culledPath,projectedDistances.get(train.id));
              projectedDistances.set(train.id,match.distance);
            }
          } else degradedTrips += 1;
          continue;
        }
        variants.set(train.id, path!.id);
        const head = decision.head!,
          match = projectMatchOnPath(
            head,
            path!,
            projectedDistances.get(train.id),
          );
        if (match.degraded) degradedTrips += 1;
        const target = match.distance;
        projectedDistances.set(train.id, target);
        const poses = placeConsist(
            path!,
            target,
            fleet,
            pitch,
          ),
          card = consistCardHtml({
            routeId: route,
            destination: train.destination,
            nextStop: train.nextStop,
            nextStopAt: train.nextStopAt,
            nowMs: options.clock?.nowMs() ?? Date.now(),
            carClass: fleet.class,
            cars: fleet.cars,
          });
        for (const car of poses) {
          // Height is evaluated once, at the car centre mapped through its span; XY and heading stay unchanged.
          const stableId=`car:${train.id}:${car.index}`, tile=geoToTile(car.position), worldMeters=geoToWorld(car.position);
          const mapped = compositionIndex?.mapPath(path!.id, car.centerMeters), onRamp = mapped ? compositionIndex!.rampAt(mapped.corridorId, mapped.c) !== undefined : false;
          // A car that maps onto a corridor takes both its height and its visibility class from the corridor profile at
          // c (ramps included), so a variant whose own alignment disagrees never snaps or flickers at a ramp end. Only
          // unmapped paths fall back to the variant alignment's nominal height and layer.
          const profile = mapped ? compositionIndex!.propertyAt(mapped.corridorId, mapped.c) : undefined;
          const elevation = mapped ? compositionIndex!.heightAt(mapped.corridorId, mapped.c) ?? ALIGNMENT_HEIGHT[car.alignment] : ALIGNMENT_HEIGHT[car.alignment];
          const visible = onRamp ? rampShown() : shown(profile?.alignment ?? car.alignment);
          const drawable = spriteDrawable(
            `car/${fleet.class}`,
            tile,
            car.heading,
            elevation,
            stableId,
          );
          dynamicPoses.set(stableId,{tile,worldMeters,alignment:car.alignment,elevation,layerVisible:visible});
          if (!visible) { hiddenDynamic.push(drawable); continue; }
          const body = assets.atlas.cars[fleet.class].bodyMeters;
          carNodes.set(stableId, { stableId, spriteId: `car/${fleet.class}`, frameIndex: drawable.frameIndex!, tile, heading: car.heading, railHeight: elevation, pitchMeters: pitch, widthMeters: body[1], alignment: car.alignment,
            corridorId: mapped?.corridorId ?? null, c: mapped?.c ?? null });
          dynamic.push(drawable);
          const [sx, sy, sw, sh] = drawable.rect,
            scale = drawable.scale!,
            x1 = drawable.screen[0] - drawable.anchor[0] * scale,
            y1 = drawable.screen[1] - drawable.anchor[1] * scale;
          carTargets.push({
            bounds: [x1, y1, x1 + sw * scale, y1 + sh * scale],
            html: card,
            tripId: train.id,
          });
          if (car.index === 0 || car.index === poses.length - 1) {
            const carSpriteId = `car/${fleet.class}` as const;
            const atlasFrame = assets.atlas.sprites[carSpriteId]
              .frames[frameFor(carSpriteId, car.heading)]!;
            overlays.set(
              drawable.stableId,
              carOverlays({
                routeId: route,
                screen: drawable.screen,
                heading: car.heading,
                socketScale: drawable.scale,
                overlayScale: state.zoom * pixelRatio,
                frame: car.index === 0
                  ? { ...atlasFrame, rearMarker: null }
                  : { ...atlasFrame, frontSign: null },
              }),
            );
          }
        }
        trainDecisions.drawn += 1;
      } catch (error) {
        trainDecisions[error instanceof TrainFeatureValidationError
          ? "rejectedValidation"
          : "rejectedVariant"] += 1;
        degradedTrips += 1;
      }
    }
    canvas.dataset.degradedTrips = String(degradedTrips);
    canvas.dataset.trainDecisions = JSON.stringify(trainDecisions);
    motion.retain(activeIds);
    assignments.retain(activeIds);
    for (const id of projectedDistances.keys()) {
      if (!activeIds.has(id)) projectedDistances.delete(id);
    }
    for (const id of variants.keys()) {
      if (!activeIds.has(id)) variants.delete(id);
    }
    const camera = { state, viewportCss: [innerWidth, innerHeight] as const, dpr: pixelRatio };
    canvas.dataset.camera=JSON.stringify(state);
    const activeLevel = levelLifecycle?.active?.level ?? 1;
    const poseFor = (item: SpriteDrawable) => dynamicPoses.get(item.stableId) ?? staticPoses.get(item.stableId) ?? ({
      tile: [0, 0] as const, worldMeters: [0, 0] as const, alignment: null,
      elevation: item.height, layerVisible: true,
    });
    const orderingStart = options.onFrameTiming ? performance.now() : 0;
    const { scene, precedenceFor, orderingMs } = orderScene(dynamic, carNodes, [0, 0, width, height]);
    const paintedIds=new Set(scene.map(item=>item.stableId));
    hiddenDynamic.push(...dynamic.filter(item=>item.stableId.startsWith('car:')&&!paintedIds.has(item.stableId)));
    const staticMembership = staticDrawables.map((item) => spriteSubmission(item, camera, activeLevel, poseFor(item)));
    const translation=cameraTranslation(camera),screenScale=state.zoom*pixelRatio,toUnit=([x,y]:readonly[number,number])=>[(x-translation[0])/screenScale,(y-translation[1])/screenScale] as const;
    const decorations:RenderDecoration[]=scene.flatMap((item)=>{
      if(!item.stableId.startsWith('car:'))return[];
      const pose=poseFor(item),result:RenderDecoration[]=(overlays.get(item.stableId)??[]).map((overlay,index)=>({kind:'decoration',instanceId:`${item.stableId}:${overlay.kind}:${index}`,parentId:item.stableId,decoration:overlay.kind,unitAnchor:toUnit(overlay.screen),unitSize:overlay.kind==='marker'?[4,4]:[10,10],angle:overlay.angle,color:overlay.color,label:overlay.label??null,diamond:overlay.diamond??false,lineWidthUnit:0,alpha:1,visible:true,pose}));
      if(hoveredTrip&&item.stableId.startsWith(`car:${hoveredTrip}:`))result.push({kind:'decoration',instanceId:`${item.stableId}:hover`,parentId:item.stableId,decoration:'hover',unitAnchor:toUnit([item.screen[0]+(item.rect[2]/2-item.anchor[0])*item.scale!,item.screen[1]+(item.rect[3]/2-item.anchor[1])*item.scale!]),unitSize:[item.rect[2]/activeLevel,item.rect[3]/activeLevel],angle:0,color:[255,242,161],label:null,diamond:false,lineWidthUnit:Math.max(1,screenScale)/screenScale,alpha:1,visible:true,pose});
      return result;
    });
    const submittedFrame=renderFrame({
      frameId: ++frameId, camera, activeLevel,
      sourceRevision: assets.atlas.sourceRevision, clearColor: [48, 54, 56], terrain: terrain && typeof terrain.submissions === 'function' ? terrain.submissions(state,[width,height],pixelRatio,terrainOpacity(visibility.isVisible("tycoon.surface"),visibility.isVisible("tycoon.underground"))) : [],
      retainedStatic: staticMembership, hidden: hiddenDynamic.map((item)=>spriteSubmission(item,camera,activeLevel,poseFor(item))), scene, poseFor, precedenceFor, decorations,
    });
    const submissionStart = options.onFrameTiming ? performance.now() : 0;
    if(renderer){renderer.render(submittedFrame);canvas.dataset.rendererFrame=JSON.stringify({frameId:submittedFrame.frameId,camera:state,width,height});}
    // Part B timing boundary: `ordering` is the precedence sort; fragment building and frame assembly count as submission.
    if(options.onFrameTiming){const end=performance.now();options.onFrameTiming({simulation:orderingStart-now,ordering:orderingMs,submission:end-submissionStart+(submissionStart-orderingStart-orderingMs),total:end-now});}
  };
  return {
    ready,
    getState: () => ({ ...state, center: [...state.center] }),
    frame,
    destroy: () => {
      destroyed = true;
      atlasScheduler.destroy();
      loadGeneration++;
      for(const lifecycle of initializingLifecycles)lifecycle.dispose();
      initializingLifecycles.clear();
      levelLifecycle?.dispose();
      renderer?.destroy();
      offVisibility();
      input.destroy();
      if(hasWindowEvents){window.removeEventListener('hashchange',applyExternalCamera);window.removeEventListener('popstate',applyExternalCamera);}
      canvas.removeEventListener("pointermove", showCar);
      canvas.removeEventListener("click", showStation);
      canvas.removeEventListener('webglcontextlost',onContextLost,true);canvas.removeEventListener('webglcontextrestored',onContextRestored,true);
      hud.destroy();
      retryButton.removeEventListener("click", retry);
      backButton.removeEventListener("click", navigateMap);
      assetError.remove();
      canvas.remove();
    },
  };
}
